/*
 * The one way the browser talks to our API.
 *
 * Everything here exists because of P22. The prototype's employee
 * client-history endpoint 500s, and the screen renders "No jobs found for this
 * client" underneath a header reading "2 Total Jobs" — a failure that arrived
 * looking like an empty result. That happens when a fetch wrapper returns
 * `[]` on error, or when a component reads `data ?? []` without ever asking
 * whether the request succeeded.
 *
 * So: this **throws** on any non-2xx, with the code and message the server
 * sent. TanStack Query turns that into `error`, and `DataTable` renders
 * `ErrorState` rather than `EmptyState`. A failed request is an error, never an
 * empty state.
 */

import type { ErrorBody } from '@shared/schemas/errors';

export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly details: unknown;

  constructor(status: number, code: string, message: string, details?: unknown) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
    this.details = details;
  }

  /**
   * Field-level messages from a Zod failure, keyed by field name.
   *
   * Returns an empty object for any other kind of error, so a form can render
   * `errors.name` without first checking what sort of failure it had.
   */
  get fieldErrors(): Record<string, string> {
    const issues = (this.details as { issues?: { field: string; message: string }[] } | undefined)
      ?.issues;
    if (!issues) return {};

    const result: Record<string, string> = {};
    for (const issue of issues) {
      // First message per field. A second one for the same field is usually a
      // knock-on of the first, and two messages under one input is noise.
      result[issue.field] ??= issue.message;
    }
    return result;
  }
}

/**
 * @param asText Return the body verbatim rather than parsing it as JSON.
 *
 * **One response that is not JSON**, and it is deliberately not: the raw mail
 * headers behind a message's Details panel. Serving them as JSON would mean
 * escaping a header block into a string and unescaping it to show, which is two
 * transformations on bytes whose whole value is being exactly what arrived.
 * Everything above this line — the credentials, the network-failure case, the
 * `ApiError` shape — is shared, because a second fetch helper is a second place
 * for the P22 contract to be got wrong.
 */
async function request<T>(path: string, init?: RequestInit, asText = false): Promise<T> {
  let response: Response;

  try {
    response = await fetch(path, {
      // The session is an HTTP-only cookie. Without this, every request is
      // anonymous and every admin screen is a 401 — and in development the API
      // is on a different port, which is when it bites.
      credentials: 'include',
      // **Never set content-type for a FormData body.** The browser has to
      // write it itself, because it carries the multipart boundary — setting
      // `application/json` over the top makes the server parse nothing and the
      // upload arrives with no file.
      headers:
        init?.body === undefined || init.body instanceof FormData
          ? undefined
          : { 'content-type': 'application/json' },
      ...init,
    });
  } catch (cause) {
    // A network failure has no status and no body. It still has to arrive as an
    // ApiError, or every caller needs a second branch for it.
    throw new ApiError(
      0,
      'network_error',
      'Could not reach the server. Check your connection and try again.',
      cause,
    );
  }

  if (response.status === 204) return undefined as T;

  const text = await response.text();
  // An error body is our JSON shape whatever the success body would have been,
  // so this is parsed either way and `asText` only decides the success return.
  const parsed: unknown = text.length > 0 ? safeJson(text) : undefined;

  if (!response.ok) {
    const body = parsed as ErrorBody | undefined;
    throw new ApiError(
      response.status,
      body?.error.code ?? 'internal_error',
      // A response that is not our JSON shape — a proxy error page, an HTML
      // 502 — still has to say something a person can read.
      body?.error.message ?? 'Something went wrong. Try again.',
      body?.error.details,
    );
  }

  return (asText ? text : parsed) as T;
}

function safeJson(text: string): unknown {
  try {
    return JSON.parse(text);
  } catch {
    return undefined;
  }
}

export const api = {
  get: <T>(path: string): Promise<T> => request<T>(path),

  /** The body verbatim — the raw mail headers, and nothing else so far. */
  getText: (path: string): Promise<string> => request<string>(path, undefined, true),

  post: <T>(path: string, body?: unknown): Promise<T> =>
    request<T>(path, {
      method: 'POST',
      body: body === undefined ? undefined : JSON.stringify(body),
    }),

  patch: <T>(path: string, body: unknown): Promise<T> =>
    request<T>(path, { method: 'PATCH', body: JSON.stringify(body) }),

  put: <T>(path: string, body: unknown): Promise<T> =>
    request<T>(path, { method: 'PUT', body: JSON.stringify(body) }),

  delete: <T>(path: string): Promise<T> => request<T>(path, { method: 'DELETE' }),

  /**
   * A multipart POST — photo uploads, and nothing else so far.
   *
   * Here rather than as a `fetch` in each dialog, which is what it was until
   * there were two of them. Going through `request` is what keeps the P22
   * contract: a failed upload throws an `ApiError` carrying the server's own
   * message ("Photos can be up to 10MB", "that photo could not be read"), so a
   * caller cannot accidentally treat a failure as a success.
   */
  upload: <T>(path: string, form: FormData): Promise<T> =>
    request<T>(path, { method: 'POST', body: form }),
};

/**
 * Build a query string, leaving out anything empty.
 *
 * `?search=` and no `search` at all mean the same thing to the server, and
 * sending the empty one makes every URL in the query cache different from the
 * one before it.
 */
export function queryString(params: Record<string, string | number | undefined>): string {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === '') continue;
    search.set(key, String(value));
  }
  const rendered = search.toString();
  return rendered.length > 0 ? `?${rendered}` : '';
}
